import { HardDrive, RotateCw } from 'lucide-react'

import { useStorage } from '@/api/hooks'
import type { StorageUsage } from '@/api/types'
import { IconButton, Skeleton } from '@/components/ui'
import { generalErrorOf } from '@/features/auth/authErrors'
import { cn } from '@/lib/cn'
import { formatBytes } from '@/lib/format'

import { Callout } from './Callout'
import { SettingsSection } from './SettingsSection'

interface Reading {
  /** Bytes the bar is measured against: the quota when set, otherwise disk capacity. */
  total: number
  percent: number
  tone: 'accent' | 'warning' | 'danger'
  caption: string
}

function read(usage: StorageUsage): Reading {
  const used = usage.quota_used_bytes ?? usage.used_bytes
  const total = usage.quota_bytes ?? used + usage.free_bytes
  const percent = total > 0 ? Math.min(100, (used / total) * 100) : 0
  const tone = percent >= 100 ? 'danger' : percent >= 90 ? 'warning' : 'accent'

  return {
    total,
    percent,
    tone,
    caption:
      total > 0
        ? `${formatBytes(used)} of ${formatBytes(total)} used${
            usage.quota_bytes === null ? ' on this disk' : ''
          }`
        : `${formatBytes(used)} used`,
  }
}

const BAR_TONE: Record<Reading['tone'], string> = {
  accent: 'bg-accent',
  warning: 'bg-warning',
  danger: 'bg-danger',
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2.5">
      <p className="text-xs text-text-muted">{label}</p>
      <p className="mt-0.5 truncate text-sm font-medium text-text">{value}</p>
    </div>
  )
}

function StorageSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-2.5 w-full rounded-full" />
      <Skeleton className="h-4 w-48" />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-[3.75rem] w-full rounded-lg" />
        ))}
      </div>
    </div>
  )
}

export function StorageSection() {
  const { data: usage, isPending, isError, error, refetch, isFetching } = useStorage()

  return (
    <SettingsSection
      icon={HardDrive}
      title="Storage"
      headerAction={
        <IconButton
          aria-label="Refresh storage usage"
          loading={isFetching && !isPending}
          onClick={() => void refetch()}
        >
          <RotateCw className="h-4 w-4" aria-hidden="true" />
        </IconButton>
      }
    >
      {isPending ? <StorageSkeleton /> : null}

      {!isPending && (isError || !usage) ? (
        <Callout tone="danger" title="Storage usage is unavailable">
          {generalErrorOf(error) ?? 'The server did not report your usage. Try refreshing.'}
        </Callout>
      ) : null}

      {!isPending && usage ? <StorageDetail usage={usage} /> : null}
    </SettingsSection>
  )
}

function StorageDetail({ usage }: { usage: StorageUsage }) {
  const reading = read(usage)
  const overQuota = usage.quota_bytes !== null && (usage.quota_used_bytes ?? usage.used_bytes) >= usage.quota_bytes
  const showOnDisk = usage.on_disk_bytes > 0 && usage.on_disk_bytes !== usage.quota_used_bytes

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div
          role="progressbar"
          aria-label="Library storage used"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(reading.percent)}
          aria-valuetext={reading.caption}
          className="h-2.5 w-full overflow-hidden rounded-full bg-surface-hover"
        >
          <div
            className={cn('h-full rounded-full transition-[width] duration-500 ease-out', BAR_TONE[reading.tone])}
            // A non-zero sliver keeps a nearly empty library visible as a bar.
            style={{ width: reading.percent > 0 ? `max(0.375rem, ${reading.percent}%)` : '0' }}
          />
        </div>
        <p className="mt-2 text-sm text-text">{reading.caption}</p>
        {showOnDisk ? (
          <p className="mt-0.5 text-xs text-text-muted">
            {formatBytes(usage.on_disk_bytes)} actually on disk, after conversion and artwork.
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Tracks" value={usage.track_count.toLocaleString()} />
        <Stat label="Saved music" value={formatBytes(usage.used_bytes)} />
        <Stat label="Free on disk" value={formatBytes(usage.free_bytes)} />
        <Stat
          label="Quota"
          value={usage.quota_bytes === null ? 'Unlimited' : formatBytes(usage.quota_bytes)}
        />
      </div>

      <p className="text-sm text-text-muted">
        Playback cache: {formatBytes(usage.cached_bytes ?? 0)} · {(usage.cached_track_count ?? 0).toLocaleString()}{' '}
        {usage.cached_track_count === 1 ? 'song' : 'songs'}.
      </p>

      {overQuota ? (
        <Callout tone="danger" title="You are at your storage quota">
          New downloads will be refused until you remove some tracks. Ask your server administrator
          to raise the quota if you need more room.
        </Callout>
      ) : null}
    </div>
  )
}
